import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { CheckCircle, XCircle, Clock, Megaphone, Mail, Calendar, Search, ExternalLink, Eye, MousePointer, MapPin, TrendingUp, Trash2 } from 'lucide-react';
import { isAdminUser, fetchAdminClaim } from '../utils/adminClaim';

function AdminAdApproval({ user, userInfo }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [authChecked, setAuthChecked] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingAd, setRejectingAd] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [stats, setStats] = useState({ total: 0, pending: 0, active: 0, rejected: 0 });

  useEffect(() => {
    const checkAuth = async () => {
      const currentUser = userInfo || user;
      const uid = currentUser?.uid;
      if (!uid) { alert('로그인이 필요합니다.'); window.location.href = '/'; return; }
      try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (!userDoc.exists()) { alert('사용자 정보를 찾을 수 없습니다.'); window.location.href = '/'; return; }
        const userData = userDoc.data();
        if (!isAdminUser({ ...userData, adminClaim: await fetchAdminClaim(auth.currentUser) })) { alert('관리자만 접근 가능합니다.'); window.location.href = '/'; return; }
        setAuthChecked(true);
      } catch (error) { console.error('권한 확인 실패:', error); alert('권한 확인 중 오류가 발생했습니다.'); window.location.href = '/'; }
    };
    checkAuth();
  }, [user, userInfo]);

  const loadApplications = useCallback(async () => {
    if (!authChecked) return;
    try {
      setLoading(true);
      let q = filter === 'all' ? query(collection(db, 'advertisements')) : query(collection(db, 'advertisements'), where('status', '==', filter));
      const snapshot = await getDocs(q);
      const apps = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      apps.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
      setApplications(apps);

      const allSnapshot = await getDocs(collection(db, 'advertisements'));
      const allApps = allSnapshot.docs.map(d => d.data());
      setStats({
        total: allApps.length,
        pending: allApps.filter(a => a.status === 'pending').length,
        active: allApps.filter(a => a.status === 'active').length,
        rejected: allApps.filter(a => a.status === 'rejected').length
      });
    } catch (error) { console.error('광고 신청서 로딩 실패:', error); alert('데이터를 불러오는데 실패했습니다.'); }
    finally { setLoading(false); }
  }, [authChecked, filter]);

  useEffect(() => { if (authChecked) loadApplications(); }, [authChecked, filter, loadApplications]);

  const handleApprove = async (application) => {
    if (!window.confirm(`"${application.title || '이 광고'}" 광고를 승인하시겠습니까?`)) return;
    try {
      setProcessing(true);
      const currentUser = userInfo || user;
      await updateDoc(doc(db, 'advertisements', application.id), { status: 'active', approvedBy: currentUser.uid, approvedAt: Timestamp.now() });
      alert('광고 승인이 완료되었습니다!');
      loadApplications();
    } catch (error) { console.error('승인 처리 실패:', error); alert('승인 처리 중 오류가 발생했습니다: ' + error.message); }
    finally { setProcessing(false); }
  };

  const handleRejectClick = (ad) => { setRejectingAd(ad); setRejectReason(''); setShowRejectModal(true); };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) { alert('거부 사유를 입력해주세요.'); return; }
    try {
      setProcessing(true);
      const currentUser = userInfo || user;
      await updateDoc(doc(db, 'advertisements', rejectingAd.id), { status: 'rejected', rejectedBy: currentUser.uid, rejectedAt: Timestamp.now(), rejectionReason: rejectReason });
      alert('광고 신청이 거부되었습니다.');
      setShowRejectModal(false);
      setRejectingAd(null);
      setRejectReason('');
      loadApplications();
    } catch (error) { console.error('거부 처리 실패:', error); alert('거부 처리 중 오류가 발생했습니다: ' + error.message); }
    finally { setProcessing(false); }
  };

  // 🆕 삭제 기능
  const handleDelete = async (application) => {
    if (!window.confirm(`"${application.title || '이 광고'}" 광고를 삭제하시겠습니까?\n\n⚠️ 이 작업은 되돌릴 수 없습니다.`)) return;
    try {
      setProcessing(true);
      await deleteDoc(doc(db, 'advertisements', application.id));
      alert('광고가 삭제되었습니다.');
      
      // 상세 모달이 열려있으면 닫기
      if (showDetailModal && selectedAd?.id === application.id) {
        setShowDetailModal(false);
        setSelectedAd(null);
      }
      
      loadApplications();
    } catch (error) { 
      console.error('삭제 처리 실패:', error); 
      alert('삭제 처리 중 오류가 발생했습니다: ' + error.message); 
    } finally { 
      setProcessing(false); 
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending: { icon: <Clock size={14} />, label: '대기중', bg: '#fef3c7', color: '#92400e' },
      active: { icon: <CheckCircle size={14} />, label: '승인됨', bg: '#d1fae5', color: '#065f46' },
      rejected: { icon: <XCircle size={14} />, label: '거부됨', bg: '#fee2e2', color: '#991b1b' }
    };
    const config = configs[status] || configs.pending;
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: config.bg, color: config.color, borderRadius: '8px', fontSize: '12px', fontWeight: '700' }}>{config.icon}{config.label}</span>;
  };

  const getTierBadge = (tier) => {
    const configs = { premium: { label: '프리미엄', bg: '#fef3c7', color: '#92400e' }, standard: { label: '스탠다드', bg: '#dbeafe', color: '#1e40af' }, basic: { label: '베이직', bg: '#f3f4f6', color: '#374151' } };
    const config = configs[tier] || configs.basic;
    return <span style={{ padding: '4px 10px', backgroundColor: config.bg, color: config.color, fontSize: '11px', fontWeight: '700', borderRadius: '6px' }}>{config.label}</span>;
  };

  const formatDate = (timestamp) => timestamp ? new Date(timestamp.toMillis()).toLocaleDateString('ko-KR') : '-';

  const filteredApplications = applications.filter(app => !searchQuery || app.title?.toLowerCase().includes(searchQuery.toLowerCase()) || app.advertiserName?.toLowerCase().includes(searchQuery.toLowerCase()));

  if (!authChecked || loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>로딩 중...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <h1 style={styles.title}><Megaphone size={32} color="#6366f1" />광고 승인 관리</h1>
        <p style={styles.subtitle}>광고 신청을 검토하고 승인/거부하세요</p>
      </div>

      {/* 통계 */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#eef2ff', color: '#6366f1' }}><Megaphone size={24} /></div>
          <div style={styles.statContent}><div style={styles.statValue}>{stats.total}</div><div style={styles.statLabel}>전체 광고</div></div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#fef3c7', color: '#f59e0b' }}><Clock size={24} /></div>
          <div style={styles.statContent}><div style={styles.statValue}>{stats.pending}</div><div style={styles.statLabel}>승인 대기</div></div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#d1fae5', color: '#10b981' }}><CheckCircle size={24} /></div>
          <div style={styles.statContent}><div style={styles.statValue}>{stats.active}</div><div style={styles.statLabel}>활성 광고</div></div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#fee2e2', color: '#ef4444' }}><XCircle size={24} /></div>
          <div style={styles.statContent}><div style={styles.statValue}>{stats.rejected}</div><div style={styles.statLabel}>거부됨</div></div>
        </div>
      </div>

      {/* 필터 & 검색 */}
      <div style={styles.controls}>
        <div style={styles.filterButtons}>
          {[{ key: 'all', label: '전체' }, { key: 'pending', label: '대기중' }, { key: 'active', label: '승인됨' }, { key: 'rejected', label: '거부됨' }].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)} style={filter === f.key ? { ...styles.filterButton, ...styles.filterButtonActive } : styles.filterButton}>{f.label}</button>
          ))}
        </div>
        <div style={styles.searchBox}>
          <Search size={20} color="#94a3b8" />
          <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="광고명, 광고주 검색..." style={styles.searchInput} />
        </div>
      </div>

      {/* 광고 목록 */}
      {filteredApplications.length === 0 ? (
        <div style={styles.emptyState}><Megaphone size={64} color="#cbd5e1" /><p style={styles.emptyText}>광고 신청 내역이 없습니다</p></div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>광고 정보</th>
                <th style={styles.th}>광고주</th>
                <th style={styles.th}>등급</th>
                <th style={styles.th}>기간</th>
                <th style={styles.th}>성과</th>
                <th style={styles.th}>상태</th>
                <th style={styles.th}>관리</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.map(app => (
                <tr key={app.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.adInfo}>
                      {app.imageUrl && <img src={app.imageUrl} alt={app.title} style={styles.adThumb} />}
                      <div>
                        <div style={styles.adTitle}>{app.title}</div>
                        <div style={styles.adPosition}><MapPin size={12} />{app.position === 'top-banner' ? '상단 배너' : app.position === 'sidebar' ? '사이드바' : '하단'}</div>
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.advertiserName}>{app.advertiserName || '-'}</div>
                    <div style={styles.advertiserEmail}>{app.advertiserInfo?.email || '-'}</div>
                  </td>
                  <td style={styles.td}>{getTierBadge(app.tier)}</td>
                  <td style={styles.td}><div style={styles.dateRange}><Calendar size={14} />{formatDate(app.startDate)} ~ {formatDate(app.endDate)}</div></td>
                  <td style={styles.td}>
                    <div style={styles.performanceStats}>
                      <span><Eye size={14} />{(app.impressions || 0).toLocaleString()}</span>
                      <span><MousePointer size={14} />{(app.clicks || 0).toLocaleString()}</span>
                      <span><TrendingUp size={14} />{app.impressions > 0 ? ((app.clicks / app.impressions) * 100).toFixed(1) : 0}%</span>
                    </div>
                  </td>
                  <td style={styles.td}>{getStatusBadge(app.status)}</td>
                  <td style={styles.td}>
                    <div style={styles.actionButtons}>
                      <button onClick={() => { setSelectedAd(app); setShowDetailModal(true); }} style={styles.actionButton} title="상세보기"><Eye size={18} /></button>
                      {app.status === 'pending' && (
                        <>
                          <button onClick={() => handleApprove(app)} style={{ ...styles.actionButton, ...styles.approveActionBtn }} disabled={processing} title="승인"><CheckCircle size={18} /></button>
                          <button onClick={() => handleRejectClick(app)} style={{ ...styles.actionButton, ...styles.rejectActionBtn }} disabled={processing} title="거부"><XCircle size={18} /></button>
                        </>
                      )}
                      {/* 🆕 삭제 버튼 */}
                      <button onClick={() => handleDelete(app)} style={{ ...styles.actionButton, ...styles.deleteActionBtn }} disabled={processing} title="삭제"><Trash2 size={18} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 상세 모달 */}
      {showDetailModal && selectedAd && (
        <div style={styles.modalOverlay} onClick={() => setShowDetailModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>광고 상세 정보</h2>
              <button onClick={() => setShowDetailModal(false)} style={styles.modalCloseButton}>×</button>
            </div>
            <div style={styles.modalBody}>
              {selectedAd.imageUrl && <img src={selectedAd.imageUrl} alt={selectedAd.title} style={styles.modalImage} />}
              <div style={styles.detailSection}>
                <h3 style={styles.detailSectionTitle}><Megaphone size={18} /> 광고 정보</h3>
                <div style={styles.detailGrid}>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>제목</span><span style={styles.detailValue}>{selectedAd.title}</span></div>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>등급</span>{getTierBadge(selectedAd.tier)}</div>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>위치</span><span style={styles.detailValue}>{selectedAd.position === 'top-banner' ? '상단 배너' : selectedAd.position === 'sidebar' ? '사이드바' : '하단'}</span></div>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>상태</span>{getStatusBadge(selectedAd.status)}</div>
                </div>
                {selectedAd.description && <div style={styles.descriptionBox}><strong>설명:</strong> {selectedAd.description}</div>}
                {selectedAd.url && <div style={styles.urlBox}><ExternalLink size={14} /><a href={selectedAd.url} target="_blank" rel="noopener noreferrer">{selectedAd.url}</a></div>}
              </div>
              <div style={styles.detailSection}>
                <h3 style={styles.detailSectionTitle}><Mail size={18} /> 광고주 정보</h3>
                <div style={styles.detailGrid}>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>광고주명</span><span style={styles.detailValue}>{selectedAd.advertiserName || '-'}</span></div>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>이메일</span><span style={styles.detailValue}>{selectedAd.advertiserInfo?.email || '-'}</span></div>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>기간</span><span style={styles.detailValue}>{formatDate(selectedAd.startDate)} ~ {formatDate(selectedAd.endDate)}</span></div>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>타겟</span><span style={styles.detailValue}>{selectedAd.targeting?.businessType === 'all' ? '전체' : selectedAd.targeting?.businessType === 'dental' ? '치과' : '기공소'}</span></div>
                </div>
              </div>
              {selectedAd.status === 'rejected' && selectedAd.rejectionReason && (
                <div style={styles.detailSection}>
                  <h3 style={styles.detailSectionTitle}><XCircle size={18} /> 거부 정보</h3>
                  <div style={styles.rejectionBox}>{selectedAd.rejectionReason}</div>
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              {selectedAd.status === 'pending' && (
                <>
                  <button onClick={() => { setShowDetailModal(false); handleApprove(selectedAd); }} style={styles.approveButtonLarge} disabled={processing}><CheckCircle size={20} /> 승인</button>
                  <button onClick={() => { setShowDetailModal(false); handleRejectClick(selectedAd); }} style={styles.rejectButtonLarge} disabled={processing}><XCircle size={20} /> 거부</button>
                </>
              )}
              {/* 🆕 상세 모달 삭제 버튼 */}
              <button onClick={() => handleDelete(selectedAd)} style={styles.deleteButtonLarge} disabled={processing}><Trash2 size={20} /> 삭제</button>
              <button onClick={() => setShowDetailModal(false)} style={styles.closeButtonLarge}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* 거부 모달 */}
      {showRejectModal && (
        <div style={styles.modalOverlay} onClick={() => setShowRejectModal(false)}>
          <div style={styles.rejectModal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>거부 사유 입력</h3>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="거부 사유를 입력해주세요..." style={styles.rejectTextarea} rows={5} />
            <div style={styles.rejectModalButtons}>
              <button onClick={handleRejectSubmit} style={styles.rejectSubmitButton} disabled={processing || !rejectReason.trim()}>거부 확정</button>
              <button onClick={() => setShowRejectModal(false)} style={styles.rejectCancelButton}>취소</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } } table tr:hover { background-color: #f8fafc; } button:hover:not(:disabled) { opacity: 0.85; } button:disabled { opacity: 0.5; cursor: not-allowed; }`}</style>
    </div>
  );
}

const styles = {
  container: { maxWidth: '1400px', margin: '0 auto', padding: '24px' },
  header: { marginBottom: '24px' },
  title: { display: 'flex', alignItems: 'center', gap: '12px', fontSize: '28px', fontWeight: '700', color: '#0f172a', margin: 0 },
  subtitle: { fontSize: '14px', color: '#64748b', margin: '8px 0 0 0' },

  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard: { display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' },
  statIcon: { width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px' },
  statContent: { flex: 1 },
  statValue: { fontSize: '24px', fontWeight: '700', color: '#0f172a' },
  statLabel: { fontSize: '13px', color: '#64748b' },

  controls: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', gap: '16px', flexWrap: 'wrap' },
  filterButtons: { display: 'flex', gap: '8px' },
  filterButton: { padding: '10px 20px', backgroundColor: '#f8fafc', color: '#64748b', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' },
  filterButtonActive: { backgroundColor: '#6366f1', color: 'white', borderColor: '#6366f1' },
  searchBox: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '10px', minWidth: '300px' },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '14px' },

  tableContainer: { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0', backgroundColor: '#f8fafc' },
  tr: { borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.2s' },
  td: { padding: '16px', fontSize: '14px', color: '#0f172a' },

  adInfo: { display: 'flex', alignItems: 'center', gap: '12px' },
  adThumb: { width: '60px', height: '45px', objectFit: 'cover', borderRadius: '8px' },
  adTitle: { fontWeight: '600', color: '#0f172a' },
  adPosition: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#64748b', marginTop: '2px' },

  advertiserName: { fontWeight: '600', color: '#0f172a' },
  advertiserEmail: { fontSize: '12px', color: '#64748b' },
  dateRange: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#64748b' },
  performanceStats: { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#64748b' },

  actionButtons: { display: 'flex', gap: '8px' },
  actionButton: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #e2e8f0', borderRadius: '8px', backgroundColor: 'white', color: '#64748b', cursor: 'pointer', transition: 'all 0.2s' },
  approveActionBtn: { borderColor: '#10b981', color: '#10b981' },
  rejectActionBtn: { borderColor: '#ef4444', color: '#ef4444' },
  deleteActionBtn: { borderColor: '#64748b', color: '#64748b' },

  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px' },
  emptyText: { fontSize: '18px', fontWeight: '600', color: '#64748b', margin: '16px 0 0 0' },

  loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px' },
  spinner: { width: '48px', height: '48px', border: '4px solid #e2e8f0', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },

  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modalContent: { backgroundColor: 'white', borderRadius: '16px', maxWidth: '700px', width: '100%', maxHeight: '90vh', overflow: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px', borderBottom: '1px solid #e2e8f0' },
  modalTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#0f172a' },
  modalCloseButton: { width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', backgroundColor: 'transparent', fontSize: '24px', color: '#64748b', cursor: 'pointer', borderRadius: '6px' },
  modalBody: { padding: '24px' },
  modalFooter: { display: 'flex', gap: '12px', padding: '24px', borderTop: '1px solid #e2e8f0', justifyContent: 'flex-end', flexWrap: 'wrap' },
  modalImage: { width: '100%', height: '200px', objectFit: 'cover', borderRadius: '12px', marginBottom: '24px' },

  detailSection: { marginBottom: '24px' },
  detailSectionTitle: { display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 16px 0', fontSize: '16px', fontWeight: '700', color: '#0f172a' },
  detailGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' },
  detailItem: { display: 'flex', flexDirection: 'column', gap: '4px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px' },
  detailLabel: { fontSize: '12px', color: '#64748b', fontWeight: '600' },
  detailValue: { fontSize: '14px', color: '#0f172a', fontWeight: '500' },
  descriptionBox: { padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', marginTop: '12px', fontSize: '14px', lineHeight: '1.6' },
  urlBox: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', backgroundColor: '#eef2ff', borderRadius: '8px', marginTop: '12px', fontSize: '14px' },
  rejectionBox: { padding: '16px', backgroundColor: '#fee2e2', borderRadius: '8px', color: '#991b1b', fontSize: '14px' },

  approveButtonLarge: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  rejectButtonLarge: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  deleteButtonLarge: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', backgroundColor: '#64748b', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  closeButtonLarge: { padding: '12px 24px', backgroundColor: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },

  rejectModal: { backgroundColor: 'white', borderRadius: '16px', padding: '24px', maxWidth: '500px', width: '100%' },
  rejectTextarea: { width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '15px', fontFamily: 'inherit', resize: 'vertical', outline: 'none', marginTop: '16px', marginBottom: '16px', boxSizing: 'border-box' },
  rejectModalButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  rejectSubmitButton: { padding: '12px 24px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  rejectCancelButton: { padding: '12px 24px', backgroundColor: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
};

export default AdminAdApproval;